import { useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import ActionDialog from "components/admin/ActionDialog";
import GameIcon from "components/admin/GameIcon";
import TemplatePicker from "components/admin/TemplatePicker";
import SelectField from "components/admin/SelectField";
import GiftEditor from "components/admin/GiftEditor";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import useConfirmChanges from "components/admin/confirmChanges";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatNumber } from "utils/format";

/** 1 Pet Coin = 100đ (docs/design/98-nap-tien.md mục 9), chỉ để admin ước giá tiền thật. */
const VND_PER_COIN = 100;

const EMPTY_ROW = { itemId: null, count: 1, price: 100, limit: 1, limitPeriod: 2 };

function PetCoinRowDialog({ row, periods, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [form, setForm] = useState(() => (row ? { ...row } : { ...EMPTY_ROW }));
  const set = (patch) => setForm({ ...form, ...patch });
  const isCard = Boolean(row?.isMonthCard);
  const submit = async () => {
    const yes = await ask({
      title: row ? `Lưu ${row.name}` : "Thêm hàng Shop Pet Coin",
      isNew: !row,
      before: row,
      after: form,
      fields: [
        ["itemId", "Vật phẩm (mã)"],
        [
          "price",
          "Giá",
          {
            text: (v) =>
              `${formatNumber(Number(v))} Pet Coin (≈ ${formatNumber(Number(v) * VND_PER_COIN)}đ)`,
          },
        ],
        ["count", "Số lượng mỗi lượt mua"],
        ["limit", "Số lượt mỗi kỳ"],
        [
          "limitPeriod",
          "Kỳ giới hạn",
          { text: (v) => periods.find((x) => x.value === Number(v))?.label || v },
        ],
      ],
      notes: [
        !row &&
          `Bán ${form.count} món giá ${formatNumber(
            Number(form.price) || 0
          )} Pet Coin (≈ ${formatNumber((Number(form.price) || 0) * VND_PER_COIN)}đ tiền thật).`,
        "Đây là hàng bán bằng tiền nạp — người chơi thấy ngay lần mở shop tiếp theo.",
      ],
    });
    if (!yes) return;
    const body = {
      itemId: Number(form.itemId) || 0,
      count: Number(form.count),
      price: Number(form.price),
      limit: Number(form.limit),
      limitPeriod: Number(form.limitPeriod),
    };
    const { ok } = await run(
      () =>
        row
          ? api.put(`/api/admin/shop/petcoin/${row.id}`, body)
          : api.post("/api/admin/shop/petcoin", body),
      "Đã lưu dòng hàng"
    );
    if (ok) {
      onClose();
      onDone();
    }
  };
  return (
    <ActionDialog
      open
      title={row ? `Sửa ${row.name}` : "Thêm hàng Shop Pet Coin"}
      description={
        isCard
          ? "Thẻ tháng chỉ đổi giá ở đây. Số ngày, quà mỗi ngày và quà lúc mua sửa ở phần Thẻ tháng."
          : "Người chơi trả bằng Pet Coin (tiền nạp). Đồ mua ra khoá giao dịch. Giới hạn lượt tính theo tài khoản: tuần từ thứ Hai, tháng từ ngày 1."
      }
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
    >
      <Grid container spacing={2}>
        {!isCard && (
          <Grid item xs={12}>
            <TemplatePicker
              kind="item"
              label="Vật phẩm"
              value={form.itemId}
              hideAdminItems
              onChange={(itemId) => set({ itemId })}
            />
          </Grid>
        )}
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Giá (Pet Coin)"
            value={form.price}
            inputProps={{ min: 1 }}
            helperText={`≈ ${formatNumber((Number(form.price) || 0) * VND_PER_COIN)}đ`}
            onChange={(e) => set({ price: e.target.value })}
          />
        </Grid>
        {!isCard && (
          <>
            <Grid item xs={6}>
              <MDInput
                fullWidth
                type="number"
                label="Số lượng mỗi lượt mua"
                value={form.count}
                inputProps={{ min: 1 }}
                onChange={(e) => set({ count: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <SelectField
                fullWidth
                label="Kỳ giới hạn"
                value={form.limitPeriod}
                onChange={(e) => set({ limitPeriod: Number(e.target.value) })}
              >
                {periods.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </SelectField>
            </Grid>
            <Grid item xs={6}>
              <MDInput
                fullWidth
                type="number"
                label="Số lượt mỗi kỳ (0 = không giới hạn)"
                value={form.limit}
                inputProps={{ min: 0 }}
                onChange={(e) => set({ limit: e.target.value })}
              />
            </Grid>
          </>
        )}
      </Grid>
    </ActionDialog>
  );
}

PetCoinRowDialog.defaultProps = { row: null };

PetCoinRowDialog.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    isMonthCard: PropTypes.bool,
  }),
  periods: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function MonthCardDialog({ card, onClose, onDone }) {
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [form, setForm] = useState(() => ({
    days: card.days,
    maxDays: card.maxDays,
    dailyGift: card.dailyGift || [],
    buyGift: card.buyGift || [],
  }));
  const set = (patch) => setForm({ ...form, ...patch });
  const submit = async () => {
    const yes = await ask({
      title: "Lưu cấu hình thẻ tháng",
      before: card,
      after: form,
      fields: [
        ["days", "Số ngày mỗi lần mua"],
        ["maxDays", "Trữ tối đa (ngày)"],
        ["dailyGift", "Quà mỗi ngày"],
        ["buyGift", "Quà lúc mua"],
      ],
      notes: ["Người đang có thẻ nhận quà mới từ ngày mai."],
    });
    if (!yes) return;
    const { ok } = await run(
      () =>
        api.put("/api/admin/shop/petcoin/month-card", {
          days: Number(form.days),
          maxDays: Number(form.maxDays),
          dailyGift: form.dailyGift,
          buyGift: form.buyGift,
        }),
      (text) => text
    );
    if (ok) {
      onClose();
      onDone();
    }
  };
  return (
    <ActionDialog
      open
      title="Cấu hình thẻ tháng"
      description="Mỗi ngày còn hạn, lần đăng nhập đầu tiên (hoặc lúc qua 0h nếu đang online) người chơi nhận quà mỗi ngày qua thư. Ngày không đăng nhập thì mất quà ngày đó."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
      maxWidth="md"
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Số ngày mỗi lần mua"
            value={form.days}
            inputProps={{ min: 1 }}
            onChange={(e) => set({ days: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Trữ tối đa (ngày) khi mua cộng dồn"
            value={form.maxDays}
            inputProps={{ min: 1 }}
            onChange={(e) => set({ maxDays: e.target.value })}
          />
        </Grid>
      </Grid>
      <GiftEditor
        value={form.dailyGift}
        onChange={(dailyGift) => set({ dailyGift })}
        label="Quà mỗi ngày"
        randomNote="Quà thẻ tháng nên cố định để người mua biết trước mình nhận gì."
      />
      <GiftEditor
        value={form.buyGift}
        onChange={(buyGift) => set({ buyGift })}
        label="Quà gửi ngay lúc mua (để trống nếu không có)"
      />
    </ActionDialog>
  );
}

MonthCardDialog.propTypes = {
  card: PropTypes.shape({
    days: PropTypes.number,
    maxDays: PropTypes.number,
    dailyGift: PropTypes.arrayOf(PropTypes.array),
    buyGift: PropTypes.arrayOf(PropTypes.array),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

const giftText = (lines) =>
  (lines || []).map((l) => l.text || l.name || JSON.stringify(l)).join(", ") || "-";

function PetCoinShopTab() {
  const canEdit = useCan(PERMISSIONS.SHOP_EDIT);
  const data = useFetch(() => api.get("/api/admin/shop/petcoin"), []);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [run] = useRunner();
  const confirm = useConfirm();
  const reload = () => data.reload(true);
  const card = data.data?.monthCard;
  const periods = data.data?.limitPeriods || [];

  const remove = async (row) => {
    const ok = await confirm({
      title: `Xoá ${row.name}?`,
      message: row.isMonthCard
        ? "Xoá dòng thẻ tháng thì người chơi không mua được thẻ nữa (thẻ đang có vẫn nhận quà tới hết hạn). Lần khởi động server tới sẽ tạo lại dòng với giá mặc định."
        : "Người chơi không còn thấy món này trong Shop Pet Coin. Lượt đã mua vẫn giữ trong DB.",
      confirmText: "Xoá",
      color: "error",
    });
    if (!ok) return;
    const { ok: done } = await run(
      () => api.del(`/api/admin/shop/petcoin/${row.id}`),
      (t) => t
    );
    if (done) reload();
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <PageCard
          title="Shop Pet Coin"
          subtitle="Mở trong game ở menu Đổi tiền. Trả bằng Pet Coin (1 Pet Coin = 100đ), đồ mua ra khoá giao dịch"
          color="info"
          noPadding
          actions={
            canEdit && (
              <MDButton size="small" color="white" onClick={() => setAdding(true)}>
                <Icon>add</Icon>&nbsp;Thêm hàng
              </MDButton>
            )
          }
        >
          {data.error && <Alert severity="error">{data.error}</Alert>}
          <AdminTable
            loading={data.loading}
            rows={data.data?.rows || []}
            rowKey="id"
            emptyText="Shop trống"
            columns={[
              {
                key: "name",
                label: "Hàng",
                render: (r) => (
                  <MDBox display="flex" gap={1} alignItems="center">
                    <GameIcon path={r.icon} size={28} fallback="inventory_2" />
                    {r.name}
                    {r.isMonthCard && <Chip size="small" color="warning" label="Thẻ tháng" />}
                  </MDBox>
                ),
              },
              { key: "count", label: "SL", align: "right" },
              {
                key: "price",
                label: "Giá",
                align: "right",
                render: (r) =>
                  `${formatNumber(r.price)} Pet Coin (≈ ${formatNumber(r.price * VND_PER_COIN)}đ)`,
              },
              {
                key: "limitText",
                label: "Giới hạn",
                render: (r) => (r.isMonthCard ? "-" : r.limitText),
              },
              canEdit && {
                key: "actions",
                label: "",
                align: "right",
                render: (r) => (
                  <>
                    <MDButton
                      size="small"
                      variant="text"
                      color="warning"
                      onClick={() => setEditing(r)}
                    >
                      <Icon>edit</Icon>&nbsp;Sửa
                    </MDButton>
                    <MDButton size="small" variant="text" color="error" onClick={() => remove(r)}>
                      <Icon>delete</Icon>
                    </MDButton>
                  </>
                ),
              },
            ].filter(Boolean)}
          />
        </PageCard>
      </Grid>
      {card && (
        <Grid item xs={12} md={6}>
          <PageCard
            title="Thẻ tháng"
            subtitle={`${formatNumber(card.active)} thẻ còn hạn, ${formatNumber(
              card.buyers
            )} tài khoản từng mua`}
            color="warning"
            actions={
              canEdit && (
                <MDButton size="small" color="white" onClick={() => setCardOpen(true)}>
                  <Icon>edit</Icon>&nbsp;Sửa
                </MDButton>
              )
            }
          >
            <MDTypography variant="button" display="block">
              Mỗi lần mua: {card.days} ngày, trữ tối đa {card.maxDays} ngày
            </MDTypography>
            <MDTypography variant="button" display="block">
              Quà mỗi ngày: {giftText(card.dailyLines)}
            </MDTypography>
            <MDTypography variant="button" display="block">
              Quà lúc mua: {giftText(card.buyLines)}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              Giá thẻ sửa ở dòng Thẻ tháng trong bảng Shop Pet Coin.
            </MDTypography>
          </PageCard>
        </Grid>
      )}
      {(adding || editing) && (
        <PetCoinRowDialog
          row={editing}
          periods={periods}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onDone={reload}
        />
      )}
      {cardOpen && card && (
        <MonthCardDialog card={card} onClose={() => setCardOpen(false)} onDone={reload} />
      )}
    </Grid>
  );
}

export default PetCoinShopTab;
